/**
 * Pawn - controllable actor that receives input from a PlayerController.
 * Owns a movement component and optional camera. Delegates input methods to equipped components.
 */

import * as THREE from 'three';

import { isEquipment } from '../components/gameplay/Equipment.js';
import { InteractionComponent } from '../components/gameplay/InteractionComponent.js';
import { DirectionalLightComponent } from '../components/lights/DirectionalLightComponent.js';
import { BasePawnMovementComponent } from '../components/movement/BasePawnMovementComponent.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { NetRuntime } from '../multiplayer/index.js';
import { NetMovementPredictor } from '../multiplayer/MovementPrediction.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { Delegate } from '../utils/Delegate.js';
import { property } from '../utils/serialization/decorator.js';

import { Actor } from './Actor.js';
import { PawnNetTransform } from './ActorNetworking.js';

import type { ActorOptions } from './Actor.js';
import type { PlayerController } from './PlayerController.js';
import type { IEquipment } from '../components/gameplay/Equipment.js';
import type {
  GamepadAxis,
  GamepadAxisEventDelegate,
  GamepadButton,
  GamepadButtonEventDelegate,
  KeyboardEventDelegate,
  MouseButton,
  MouseEventDelegate
} from '../systems/InputManager.js';

export interface PawnOptions extends ActorOptions {
  // should only be null if the pawn explicitly does not want input based movement logic (VR, etc.)
  movementComponent: BasePawnMovementComponent | null,
}

export type PawnPossessDelegate = Delegate<[Pawn, PlayerController], void>;

@EngineClass('Pawn')
export class Pawn extends Actor {

  private _movementComponent: BasePawnMovementComponent | null = null;
  protected movementPredictor: NetMovementPredictor | null = null;

  public override get replicated() {
    return super.replicated;
  }

  public override set replicated(value: boolean) {
    super.replicated = value;
    if (this.replicated && this._movementComponent) {
      this._movementComponent.setReplicated(true);
    }
  }

  @property({ description: 'The component that the pawn uses to move.' })
  public get movementComponent(): BasePawnMovementComponent | null {
    return this._movementComponent;
  }

  public set movementComponent(component: BasePawnMovementComponent | null) {
    if (this._movementComponent === component) return;
    if (!this.isEditor) {
      if (this._movementComponent) {
        this._movementComponent.removeFromParent();
      }
      if (component) {
        this.rootComponent.add(component);
      }
    }
    if (component && this.replicated) {
      component.setReplicated(true);
    }
    this._movementComponent = component;
  }

  @property({ replicate: true, hidden: true, transient: true })
  private playerController: PlayerController | null = null;

  @property({ replicate: true, transient: true, hidden: true })
  protected pawnNetTransform: PawnNetTransform = new PawnNetTransform();

  public readonly onKeyDown: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onKeyUp: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onMouseDown: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();
  public readonly onMouseUp: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();

  public readonly onGamepadButtonDown: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadButtonUp: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadAxisChange: GamepadAxisEventDelegate = new Delegate<[number, GamepadAxis, number], boolean>();
  public readonly onPossessed: PawnPossessDelegate = new Delegate<[Pawn, PlayerController], void>();
  public readonly onUnpossessed: PawnPossessDelegate = new Delegate<[Pawn, PlayerController], void>();

  private directionalLight: DirectionalLightComponent | null = null;

  @property()
  public enableDirectionalLightFollowing: boolean = true;

  public override setRootComponent(newRootComponent: SceneComponent, copyTransform: boolean): this {
    super.setRootComponent(newRootComponent, copyTransform);
    if (this.movementComponent) {
      // move the movement component to the new root
      this.rootComponent.add(this.movementComponent);
    }
    return this;
  }

  public override initialize(options?: PawnOptions): void {
    super.initialize(options);

    this.replicated = true;

    if (options?.movementComponent) {
      this.movementComponent = options.movementComponent;
    }
  }

  public addForwardInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addForwardInput(value, isAbsolute);
  }

  public addRightInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addRightInput(value, isAbsolute);
  }

  public addLookUpInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addLookUpInput(value, isAbsolute);
  }

  public addLookRightInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addLookRightInput(value, isAbsolute);
  }

  public addZoomInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addZoomInput(value, isAbsolute);
  }

  public getMovementPredictor(): NetMovementPredictor | null {
    return this.movementPredictor;
  }

  private getOrCreateMovementPredictor(): NetMovementPredictor {
    this.movementPredictor ??= new NetMovementPredictor();
    return this.movementPredictor;
  }

  //====================================================================================================

  public override getPlayerController(): PlayerController | null {
    return this.playerController;
  }

  /**
   * Handles jump input. For autonomous proxies the jump flag travels in the sequenced
   * input packet (via PlayerController → BasePawnMovementComponent). This method is
   * used for direct (non-networked / listen-server) play paths.
   */
  public handleJump(strength: number = 1): void {
    this.jump(strength);
  }

  /** Handles stop-jump input for direct (non-networked) play paths. */
  public handleStopJump(): void {
    this.stopJump();
  }

  public jump(strength: number = 1) {
    this.movementComponent?.jump(strength);
  }

  public stopJump() {
    this.movementComponent?.stopJump();
  }

  public beginFire(): boolean {
    return this.callEquipment('beginFire');
  }

  public endFire(): boolean {
    return this.callEquipment('endFire');
  }

  public beginAltFire(): boolean {
    return this.callEquipment('beginAltFire');
  }

  public endAltFire(): boolean {
    return this.callEquipment('endAltFire');
  }

  public reload(): boolean {
    return this.callEquipment('reload');
  }

  /**
   * Called by PlayerController when the interaction key is pressed.
   * Forwards to the InteractionComponent to handle the interaction.
   * @returns True if an interaction was started, false otherwise
   */
  public beginInteract(): boolean {
    return this.getInteractionComponent()?.beginInteract() ?? false;
  }

  /**
   * Called by PlayerController when the interaction key is released.
   * Forwards to the InteractionComponent to end any active interaction.
   */
  public endInteract(): void {
    this.getInteractionComponent()?.endInteract();
  }

  /**
   * Gets the InteractionComponent attached to this pawn.
   * @returns The InteractionComponent or null if not found
   */
  public getInteractionComponent(): InteractionComponent | null {
    return this.rootComponent.getComponent(InteractionComponent);
  }

  public override setWorldTransform(transform: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3
  }): void {
    if (this.movementComponent) {
      this.movementComponent.setPawnWorldTransform(transform);
    } else {
      super.setWorldTransform(transform);
    }
  }

  public handleKeyDown(e: KeyboardEvent): boolean {
    return this.onKeyDown.invokeWithPredicate(result => result, e).at(-1) ?? false;
  }

  public handleKeyUp(e: KeyboardEvent): boolean {
    return this.onKeyUp.invokeWithPredicate(result => result, e).at(-1) ?? false;
  }

  public handleMouseDown(button: MouseButton, e: MouseEvent): boolean {
    return this.onMouseDown.invokeWithPredicate(result => result, button, e).at(-1) ?? false;
  }

  public handleMouseUp(button: MouseButton, e: MouseEvent): boolean {
    return this.onMouseUp.invokeWithPredicate(result => result, button, e).at(-1) ?? false;
  }

  /**
   * Handles mouse wheel events. Can be overridden by subclasses to implement zoom/scrolling.
   * @param e - The wheel event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseWheel(e: WheelEvent): boolean {
    if (this.movementComponent?.handleMouseWheel(e)) {
      return true;
    }
    return false;
  }

  public handleGamepadButtonDown(gamepadIndex: number, buttonIndex: GamepadButton, value: number): boolean {
    return this.onGamepadButtonDown.invokeWithPredicate(result => result, gamepadIndex, buttonIndex, value).at(-1) ?? false;
  }

  public handleGamepadButtonUp(gamepadIndex: number, buttonIndex: GamepadButton): boolean {
    return this.onGamepadButtonUp.invokeWithPredicate(result => result, gamepadIndex, buttonIndex, 0).at(-1) ?? false;
  }

  public handleGamepadAxisChange(gamepadIndex: number, axisIndex: GamepadAxis, value: number): boolean {
    return this.onGamepadAxisChange.invokeWithPredicate(result => result, gamepadIndex, axisIndex, value).at(-1) ?? false;
  }

  public override isNetRelevantFor(clientId?: string, clientData?: {
    position?: THREE.Vector3Like;
    [key: string]: any
  }): boolean {
    return this.playerController !== null || super.isNetRelevantFor(clientId, clientData);
  }

  public onRepPawnNetTransform(): void {
    if (!this.isSimulatedProxy()) return;

    if (this.isPhysicsEnabled()) {
      this.setPhysicsEnabled(false);
    }
    this.getOrCreateMovementPredictor().addRemoteTransform({
      timestamp: this.pawnNetTransform.timestamp || performance.now(),
      position: this.pawnNetTransform.position.clone(),
      rotation: new THREE.Quaternion().setFromEuler(this.pawnNetTransform.rotation),
      scale: this.pawnNetTransform.scale.clone(),
    });
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // Make the directional light follow the pawn when it's possessed
    if (this.enableDirectionalLightFollowing && this.getPlayerController()) {
      if (!this.directionalLight) {
        const actors = this.getWorld()?.getActors() ?? [];
        for (const actor of actors) {
          this.directionalLight = actor.getComponent(DirectionalLightComponent);
          if (this.directionalLight) break;
        }
      }
      if (this.directionalLight) {
        this.directionalLight.moveTargetTo(this.getWorldPosition());
      }
    }
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    if (!this.isSimulatedProxy()) {
      this.pawnNetTransform.position.copy(this.rootComponent.getWorldPosition());
      this.pawnNetTransform.rotation.copy(this.rootComponent.getWorldRotation());
      this.pawnNetTransform.scale.copy(this.rootComponent.getWorldScale());
      this.pawnNetTransform.timestamp = NetMovementPredictor.currentTime();
    }
  }

  //====================================================================================================

  protected callEquipment(method: keyof IEquipment): boolean {
    for (const component of this.getComponents(SceneComponent)) {
      if (isEquipment(component) && component[method]()) {
        return true;
      }
    }
    return false;
  }

  protected override doBeginPlay(): void {
    super.doBeginPlay();
    this.getWorld()?.consoleManager.addCommandHandler(this);

    // Auto-create InteractionComponent if not already present
    if (!this.rootComponent.getComponent(InteractionComponent)) {
      this.rootComponent.add(InteractionComponent.create({}));
    }
  }

  protected override doEndPlay(): void {
    super.doEndPlay();
    this.getWorld()?.consoleManager.removeCommandHandler(this);
  }

  @ConsoleCommand('jump', 'Make the pawn jump (usage: jump [strength])')
  private jumpCommand(strength: string = '1'): string {
    this.jump(Number(strength));
    return 'Jumped';
  }

  public _privateSetPlayerController(playerController: PlayerController | null) {
    if (this.playerController === playerController) {
      return;
    }

    if (this.playerController) {
      this.onUnpossessed.invoke(this, this.playerController);
    }

    this.playerController = playerController;

    if (this.playerController) {
      this.onPossessed.invoke(this, this.playerController);
      if (!NetRuntime.isClient()) {
        this.replicated = true;
        this.setAutonomousProxy(true);
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Pawn';
  }
}
